import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { marked } from 'marked';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import { useCustomers } from '../context/CustomerContext';
import { displayName } from '../utils/customerDisplay';
import { orderAfterDrop } from '../utils/cardOrder';
import './TrialPipelinePage.css';

interface Stage {
  id: number;
  name: string;
  display_order: number;
  target_day: number;
  is_terminal: boolean;
  auto_advance: boolean;
  on_enter_template_id: number | null;
  on_enter_name: string | null;
  during_template_id: number | null;
  during_name: string | null;
  during_frequency_days: number;
  on_exit_template_id: number | null;
  on_exit_name: string | null;
}

interface Template {
  id: number;
  name: string;
  subject: string;
  body_html: string;
  template_type: string;
}

interface TrialCustomer {
  id: number;
  customer_tag: string;
  customer_name: string;
  customer_email: string;
  current_stage_id: number;
  stage_name: string;
  display_order: number;
  is_terminal: boolean;
  trial_start_date: string | null;
  trial_target_end_date: string | null;
  sort_order: number | null;
  ae_name: string | null;
  ae_email: string | null;
  sf_last_opp_name: string | null;
  salesforce_url: string | null;
  salesforce_link_type: 'opportunity' | 'account' | null;
  days_elapsed: number;
  hours_in_current_stage: number;
  auto_advance_enabled: boolean;
  assigned_rep: string | null;
  status: string;
  notes: string | null;
  to_emails: string | null;
  to_names: string | null;
  cc_emails: string | null;
  meeting_date: string | null;
  history?: any[];
  emails?: any[];
}

interface DashboardMetrics {
  active_trials: number;
  converted: number;
  churned: number;
  conversion_rate: number;
  avg_days_to_convert: number;
  at_risk: number;
  per_stage: { id: number; name: string; count: number }[];
  sandbox_mode: boolean;
}

interface UsageStats {
  customer_tag: string;
  org_name: string;
  device_usage: {
    dau: number;
    wau: number;
    mau: number;
    total_devices: number;
    utilization: number;
    daily_trend: { date: string; active_devices: number }[];
  };
  org_stats: {
    escalations?: number;
    reports?: number;
    videos_total?: number;
    activation_rate?: number;
    actions?: Record<string, number>;
    report_video_duration?: { days: number; hours: number; minutes: number };
    total_video_duration?: { days: number; hours: number; minutes: number };
  };
}

interface FollowUpRule {
  id?: number;
  name: string;
  trigger_condition: string;
  days_threshold: number;
  email_template_id: number | null;
  template_name?: string;
  is_final_warning: boolean;
  is_active: boolean;
}

interface Shipment {
  tracking_number: string;
  carrier: string;
  status: string | null;
  shipped_date: string;
  estimated_arrival: string;
  product_name: string | null;
  service_name: string | null;
  tracking_url: string;
}

function progressColor(elapsed: number, target: number): string {
  if (target <= 0) return 'var(--color-danger)';
  const pct = elapsed / target;
  if (pct < 0.5) return 'var(--color-success)';
  if (pct < 0.8) return 'var(--color-warning)';
  return 'var(--color-danger)';
}

function trialLengthDays(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  const ms = e.getTime() - s.getTime();
  if (ms <= 0) return null;
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const raw = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z';
  const d = new Date(raw);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatVideoDuration(dur: { days: number; hours: number; minutes: number }): string {
  const parts: string[] = [];
  if (dur.days > 0) parts.push(`${dur.days}d`);
  if (dur.hours > 0) parts.push(`${dur.hours}h`);
  if (dur.minutes > 0) parts.push(`${dur.minutes}m`);
  return parts.join(' ') || '0m';
}

function htmlToMarkdown(html: string): string {
  // Inline markup first so list items keep bold / italic / link syntax
  // when we walk into the list bodies below.
  let text = html
    .replace(/<strong>/gi, '**').replace(/<\/strong>/gi, '**')
    .replace(/<em>/gi, '*').replace(/<\/em>/gi, '*')
    .replace(/<a\s+href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, '[$2]($1)');

  // Walk <ol>/<ul> blocks individually so numbered lists round-trip as
  // 1./2./... and bulleted lists as -. The previous implementation
  // collapsed both into "- ", which made any numbered list silently
  // become unordered on the next save.
  const renderList = (body: string, ordered: boolean): string => {
    const items: string[] = [];
    let i = 0;
    body.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_match, item) => {
      i += 1;
      const cleaned = String(item)
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      items.push((ordered ? `${i}. ` : '- ') + cleaned);
      return '';
    });
    return '\n' + items.join('\n') + '\n';
  };
  text = text
    .replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_m, body) => renderList(body, true))
    .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_m, body) => renderList(body, false));

  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '\n')
    .replace(/<h[1-3]>/gi, '### ')
    .replace(/<\/h[1-3]>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&middot;/g, '\u00b7')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const PREVIEW_DEFAULTS: Record<string, string> = {
  '{{customer_name}}': 'Bill Thompson',
  '{{customer_first_name}}': 'Bill',
  '{{recipient_first_names}}': 'Bill and Sarah',
  '{{company_name}}': 'Olinn Security Inc.',
  '{{customer_email}}': 'bill@olinnsecurity.com',
  '{{rep_name}}': 'Sunny Lee',
  '{{trial_days_elapsed}}': '6',
  '{{trial_days_remaining}}': '4',
  '{{stage_name}}': 'Mid-Pilot Review',
  '{{trial_end_date}}': 'Apr 24, 2026',
  '{{onboarding_link}}': 'https://calendar.app.google/example',
  '{{dau}}': '3.2',
  '{{wau}}': '8',
  '{{mau}}': '12',
  '{{total_devices}}': '15',
  '{{utilization}}': '80',
  '{{escalations}}': '47',
  '{{reports}}': '12',
  '{{videos}}': '156',
  '{{video_duration}}': '38h 12m',
  '{{activation_rate}}': '85',
  '{{usage_stats_block}}': '<table style="border-collapse:collapse;width:100%;max-width:500px;margin:12px 0;"><tr style="background:#f5f5f5;"><td style="padding:6px 10px;border:1px solid #ddd;font-weight:600;">Metric</td><td style="padding:6px 10px;border:1px solid #ddd;font-weight:600;">Value</td></tr><tr><td style="padding:6px 10px;border:1px solid #ddd;">DAU</td><td style="padding:6px 10px;border:1px solid #ddd;">3.2</td></tr><tr><td style="padding:6px 10px;border:1px solid #ddd;">WAU</td><td style="padding:6px 10px;border:1px solid #ddd;">8</td></tr><tr><td style="padding:6px 10px;border:1px solid #ddd;">Escalations</td><td style="padding:6px 10px;border:1px solid #ddd;">47</td></tr><tr><td style="padding:6px 10px;border:1px solid #ddd;">Reports</td><td style="padding:6px 10px;border:1px solid #ddd;">12</td></tr><tr><td style="padding:6px 10px;border:1px solid #ddd;">Videos</td><td style="padding:6px 10px;border:1px solid #ddd;">156</td></tr></table>',
  '{{trial_start_date}}': '5/1/2026',
  '{{checkin_trial_start_date}}': '5/1/2026',
  '{{checkin_onboarding_date}}': '5/1/2026',
  '{{checkin_mid_pilot_review_date}}': '5/5/2026',
  '{{checkin_pilot_review_date}}': '5/8/2026',
  '{{checkin_pilot_complete_date}}': '5/10/2026',
  '{{project_plan_block}}': '<table style="border-collapse:collapse;width:100%;max-width:600px;margin:12px 0;"><tr style="background:#f5f5f5;"><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Milestone</th><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Target Date</th><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Status</th></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;">Trial Start</td><td style="padding:8px 12px;border:1px solid #ddd;">5/1/2026</td><td style="padding:8px 12px;border:1px solid #ddd;color:#0a7d3b;font-weight:600;">Complete</td></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;">Day 1: Onboarding</td><td style="padding:8px 12px;border:1px solid #ddd;">5/1/2026</td><td style="padding:8px 12px;border:1px solid #ddd;color:#0a7d3b;font-weight:600;">Complete</td></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;">Day 5: Mid-Pilot Review</td><td style="padding:8px 12px;border:1px solid #ddd;">5/5/2026</td><td style="padding:8px 12px;border:1px solid #ddd;color:#1a56db;font-weight:600;">In Progress</td></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;">Day 8: Pilot Review</td><td style="padding:8px 12px;border:1px solid #ddd;">5/8/2026</td><td style="padding:8px 12px;border:1px solid #ddd;color:#666;">Not Started</td></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;">Day 10: Pilot Complete</td><td style="padding:8px 12px;border:1px solid #ddd;">5/10/2026</td><td style="padding:8px 12px;border:1px solid #ddd;color:#666;">Not Started</td></tr></table>',
  '{{latest_tracking_link}}': 'https://www.ups.com/track?tracknum=1Z999AA10123456784',
  '{{tracking_links_block}}': '<table style="border-collapse:collapse;width:100%;max-width:600px;margin:12px 0;"><tr style="background:#f5f5f5;"><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Carrier</th><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Tracking</th><th style="padding:8px 12px;border:1px solid #ddd;text-align:left;">Details</th></tr><tr><td style="padding:8px 12px;border:1px solid #ddd;font-weight:600;">UPS</td><td style="padding:8px 12px;border:1px solid #ddd;"><a href="https://www.ups.com/track?tracknum=1Z999AA10123456784">Track shipment</a></td><td style="padding:8px 12px;border:1px solid #ddd;color:#555;font-size:13px;">1Z999AA10123456784 · On the Way · 2026-05-02</td></tr></table>',
};

function renderPreview(markdown: string): string {
  let text = markdown;
  for (const [key, val] of Object.entries(PREVIEW_DEFAULTS)) {
    text = text.split(key).join(val);
  }
  return marked.parse(text) as string;
}

const BASE_TEMPLATE_VARS = [
  { label: 'Customer', vars: ['{{customer_name}}', '{{customer_first_name}}', '{{recipient_first_names}}', '{{company_name}}', '{{customer_email}}'] },
  { label: 'Trial', vars: ['{{trial_days_elapsed}}', '{{trial_days_remaining}}', '{{stage_name}}', '{{rep_name}}'] },
  { label: 'Shipments', vars: ['{{tracking_links_block}}', '{{latest_tracking_link}}'] },
  { label: 'Usage', vars: ['{{dau}}', '{{wau}}', '{{mau}}', '{{total_devices}}', '{{utilization}}', '{{escalations}}', '{{reports}}', '{{videos}}', '{{video_duration}}', '{{activation_rate}}', '{{usage_stats_block}}'] },
];

const slugifyStage = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

const TrialPipelinePage: React.FC = () => {
  // Resolve the displayed customer name from the Stripe description (refreshed
  // on every sync) so a rename in Stripe shows here too. customer_tag stays the
  // stable key; fall back to the trial payload's own name fields for prospects
  // not yet in the customers table.
  const { customers: allCustomers } = useCustomers();
  const customerByTag = useMemo(
    () => new Map(allCustomers.map(c => [c.customer_tag, c])),
    [allCustomers],
  );
  const trialName = useCallback(
    (c: { customer_tag: string; customer_name?: string; stripe_name?: string }): string => {
      const ctx = customerByTag.get(c.customer_tag);
      if (ctx) return displayName(ctx);
      return c.customer_name || c.stripe_name || c.customer_tag;
    },
    [customerByTag],
  );
  const [activeTab, setActiveTab] = useState<'dashboard' | 'config'>('dashboard');
  const [stages, setStages] = useState<Stage[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [customers, setCustomers] = useState<TrialCustomer[]>([]);
  const [dashboard, setDashboard] = useState<DashboardMetrics | null>(null);
  const [followUpRules, setFollowUpRules] = useState<FollowUpRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<TrialCustomer | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editorMarkdown, setEditorMarkdown] = useState('');
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [noteText, setNoteText] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<number | null>(null);
  const [emailPreview, setEmailPreview] = useState<{ subject: string; body_html: string; to_emails: string; cc_emails: string } | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const previewBodyRef = useRef<HTMLDivElement>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [copiedTracking, setCopiedTracking] = useState<string | null>(null);
  const [editToEmails, setEditToEmails] = useState('');
  const [editToNames, setEditToNames] = useState('');
  const [editCcEmails, setEditCcEmails] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [showAllEmails, setShowAllEmails] = useState(false);
  const [advancingCustomerId, setAdvancingCustomerId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<number | null>(null);
  // Which card the pointer is over while dragging, and whether the drop lands
  // after it — together these give the insertion point for manual ordering.
  const [dragOverCardId, setDragOverCardId] = useState<number | null>(null);
  const [dropAfter, setDropAfter] = useState<boolean>(false);
  const [aeFilter, setAeFilter] = useState<string>('');
  // Direction of the default (trial start date) sort. A stage arranged by hand
  // ignores this — its manual order wins until it is reset.
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => (localStorage.getItem('trialSortDir') === 'desc' ? 'desc' : 'asc'));
  const [sendingEmailId, setSendingEmailId] = useState<number | null>(null);
  const [bulkSendingStage, setBulkSendingStage] = useState<number | null>(null);
  const [bulkTemplateSelect, setBulkTemplateSelect] = useState<Record<number, number | null>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [inactiveCustomers, setInactiveCustomers] = useState<any[]>([]);
  const [showReactivate, setShowReactivate] = useState(false);
  const [reactivatingTag, setReactivatingTag] = useState<string | null>(null);
  const [reactivateDays, setReactivateDays] = useState(10);
  const [newStageName, setNewStageName] = useState('');
  const [newStageTarget, setNewStageTarget] = useState(3);

  const api = useCallback(async (path: string, opts?: RequestInit) => {
    const res = await fetch(`${API_BASE_URL}/api/trial-pipeline${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...opts?.headers },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Request failed: ${res.status}`);
    }
    return res.json();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, d, t, r] = await Promise.all([
        api('/stages'),
        api(`/customers?status=active&sort_dir=${sortDir}`),
        api('/dashboard'),
        api('/templates'),
        api('/followup-rules').catch(() => []),
      ]);
      setStages(s);
      setCustomers(c);
      setDashboard(d);
      setTemplates(t);
      setFollowUpRules(r);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, sortDir]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Pull shipments for the customer whose detail modal is open.
  useEffect(() => {
    if (!selectedCustomer) {
      setShipments([]);
      return;
    }
    let cancelled = false;
    setShipmentsLoading(true);
    api(`/customers/${selectedCustomer.id}/shipments`)
      .then((rows: Shipment[]) => { if (!cancelled) setShipments(rows || []); })
      .catch(() => { if (!cancelled) setShipments([]); })
      .finally(() => { if (!cancelled) setShipmentsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCustomer, api]);

  // Render the chosen template against the selected customer's real
  // variables so the user sees first names, project plan block, etc.
  // exactly as they will land in the recipient's inbox.
  useEffect(() => {
    if (!selectedCustomer || !selectedEmailTemplate) {
      setEmailPreview(null);
      setEditedSubject('');
      return;
    }
    let cancelled = false;
    setEmailPreviewLoading(true);
    api(`/customers/${selectedCustomer.id}/preview-email`, {
      method: 'POST',
      body: JSON.stringify({ template_id: selectedEmailTemplate }),
    })
      .then((preview: any) => {
        if (cancelled) return;
        setEmailPreview(preview);
        setEditedSubject(preview?.subject || '');
      })
      .catch(() => { if (!cancelled) setEmailPreview(null); })
      .finally(() => { if (!cancelled) setEmailPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCustomer, selectedEmailTemplate, api]);

  // Project-plan check-ins drive the Milestones variable group. Active,
  // non-terminal stages become {{checkin_<slug>_date}} variables.
  const templateVarGroups = useMemo(() => {
    const milestoneVars = ['{{trial_start_date}}', '{{project_plan_block}}'];
    stages
      .filter(s => !s.is_terminal)
      .sort((a, b) => a.display_order - b.display_order)
      .forEach(s => {
        const slug = slugifyStage(s.name);
        if (slug) milestoneVars.push(`{{checkin_${slug}_date}}`);
      });
    const byLabel = (l: string) => BASE_TEMPLATE_VARS.find(g => g.label === l)!;
    return [
      byLabel('Customer'),
      byLabel('Trial'),
      { label: 'Milestones', vars: milestoneVars },
      byLabel('Shipments'),
      byLabel('Usage'),
    ];
  }, [stages]);

  const moveStage = async (stageId: number, direction: -1 | 1) => {
    const ordered = [...stages].filter(s => !s.is_terminal).sort((a, b) => a.display_order - b.display_order);
    const idx = ordered.findIndex(s => s.id === stageId);
    const swapIdx = idx + direction;
    if (idx === -1 || swapIdx < 0 || swapIdx >= ordered.length) return;
    const a = ordered[idx], b = ordered[swapIdx];
    try {
      await Promise.all([
        api(`/stages/${a.id}`, { method: 'PUT', body: JSON.stringify({ ...a, display_order: b.display_order }) }),
        api(`/stages/${b.id}`, { method: 'PUT', body: JSON.stringify({ ...b, display_order: a.display_order }) }),
      ]);
      setStages(await api('/stages'));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteStage = async (stage: Stage) => {
    if (!window.confirm(`Delete stage "${stage.name}"? Past customer history is preserved.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/trial-pipeline/stages/${stage.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Delete failed: ${res.status}`);
      }
      setStages(await api('/stages'));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteTemplate = async (template: Template) => {
    if (!window.confirm(`Delete template "${template.name}"? You can't undo this from the UI.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/trial-pipeline/templates/${template.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Delete failed: ${res.status}`);
      }
      setEditingTemplate(null);
      setEditorMarkdown('');
      setTemplates(await api('/templates'));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const refreshBoard = async () => {
    try {
      const [c, d] = await Promise.all([
        api(`/customers?status=active&sort_dir=${sortDir}`), api('/dashboard')]);
      setCustomers(c);
      setDashboard(d);
    } catch {}
  };

  // Drop a stage back to date ordering, so the asc/desc toggle applies to it
  // again. Without this one drag would pin the column to manual forever.
  const clearStageOrder = async (stageId: number) => {
    try {
      await api('/customers/reorder/clear', {
        method: 'POST',
        body: JSON.stringify({ stage_id: stageId }),
      });
      await refreshBoard();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Persist the manual order of one stage. The board is re-fetched after, so
  // the server's ordering is the single source of truth rather than local state.
  const reorderStage = async (stageId: number, orderedIds: number[]) => {
    try {
      await api('/customers/reorder', {
        method: 'POST',
        body: JSON.stringify({ stage_id: stageId, ordered_ids: orderedIds }),
      });
      await refreshBoard();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const advanceCustomer = async (id: number, stageId: number) => {
    if (advancingCustomerId) return;
    setAdvancingCustomerId(id);
    const stageName = stages.find(s => s.id === stageId)?.name || 'next stage';
    // Optimistic update — move card instantly
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, current_stage_id: stageId, stage_name: stageName } : c));
    setToast(`Moved to ${stageName}`);
    setTimeout(() => setToast(null), 3000);
    setAdvancingCustomerId(null);
    // Fire API and refresh in background
    try {
      await api(`/customers/${id}/advance`, {
        method: 'POST',
        body: JSON.stringify({ stage_id: stageId, send_email: true }),
      });
      refreshBoard();
    } catch (err: any) {
      setError(err.message);
      refreshBoard(); // revert to server state on error
    }
  };

  const updateCustomer = async (id: number, updates: Record<string, any>): Promise<boolean> => {
    try {
      await api(`/customers/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
      await refreshBoard();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const openDetail = (c: TrialCustomer) => {
    // Render the panel immediately with the data we already have on the card —
    // the list endpoint returns every field shown in the header/meta/email-edit blocks.
    setSelectedCustomer(c);
    setEditToEmails(c.to_emails || '');
    setEditToNames(c.to_names || '');
    setEditCcEmails(c.cc_emails || '');
    setEditStartDate(c.trial_start_date || '');
    setEditEndDate(c.trial_target_end_date || '');
    setSelectedEmailTemplate(null);
    setShowAllEmails(false);

    // Fetch history + emails in the background, merge in once available.
    api(`/customers/${c.id}`)
      .then((detail: TrialCustomer) => {
        setSelectedCustomer(prev => (prev && prev.id === c.id ? { ...prev, history: detail.history, emails: detail.emails } : prev));
      })
      .catch(() => {});

    // No caching — always fetch fresh usage stats whenever the modal opens.
    setUsageStats(null);
    setUsageLoading(true);
    api(`/customers/${c.id}/usage-stats?_t=${Date.now()}`)
      .then(stats => setUsageStats(stats))
      .catch(() => setUsageStats(null))
      .finally(() => setUsageLoading(false));
  };

  const isTerminal = (s: Stage) => Boolean(s.is_terminal);
  const nonTerminalStages = stages.filter(s => !isTerminal(s)).sort((a, b) => a.display_order - b.display_order);
  // Distinct AEs present on the board, for the quick filter.
  const aeOptions = useMemo(() => {
    const names = new Set<string>();
    customers.forEach(c => { if (c.ae_name) names.add(c.ae_name); });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [customers]);

  const visibleCustomers = useMemo(
    () => (aeFilter ? customers.filter(c => c.ae_name === aeFilter) : customers),
    [customers, aeFilter],
  );

  const customersByStage: Record<number, TrialCustomer[]> = {};
  for (const s of nonTerminalStages) {
    customersByStage[s.id] = visibleCustomers.filter(c => c.current_stage_id === s.id);
  }

  /* ─── Detail Panel ─── */
  const renderDetail = () => {
    if (!selectedCustomer) return null;
    const c = selectedCustomer;
    return (
      <div className="trial-detail-overlay" onClick={() => setSelectedCustomer(null)}>
        <div className="trial-detail-panel" onClick={e => e.stopPropagation()}>
          <div className="trial-detail-header">
            <div>
              <h3>{trialName(c)}</h3>
              <span className="trial-detail-tag">{c.customer_tag}</span>
              {c.salesforce_url && (
                <a
                  className="trial-sf-link"
                  href={c.salesforce_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={c.sf_last_opp_name || undefined}
                >
                  {c.salesforce_link_type === 'opportunity'
                    ? 'Opportunity in Salesforce'
                    : 'Account in Salesforce'} ↗
                </a>
              )}
            </div>
            <button className="trial-detail-close" onClick={() => setSelectedCustomer(null)}>&times;</button>
          </div>

          <div className="trial-detail-meta">
            <div><span className="meta-label">Rep</span>{c.assigned_rep || '-'}</div>
            <div><span className="meta-label">AE</span>{c.ae_name || '-'}</div>
            <div className="meta-stage-field">
              <span className="meta-label">Stage</span>
              <select className="meta-stage-select" value={c.current_stage_id} onChange={e => {
                const stageId = Number(e.target.value);
                if (stageId !== c.current_stage_id) {
                  advanceCustomer(c.id, stageId);
                  setSelectedCustomer({ ...c, current_stage_id: stageId, stage_name: stages.find(s => s.id === stageId)?.name || '' });
                }
              }}>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {c.meeting_date && <div><span className="meta-label">Onboarding</span>{formatDate(c.meeting_date)}</div>}
            <div className="meta-date-field">
              <span className="meta-label">Trial Start</span>
              <input type="date" value={editStartDate} onChange={e => {
                setEditStartDate(e.target.value);
                // Allow clearing (e.g. onboarding not yet scheduled) — send null
                // so the date is unset rather than skipping the update.
                updateCustomer(c.id, { trial_start_date: e.target.value || null });
              }} />
            </div>
            <div className="meta-date-field">
              <span className="meta-label">Target End</span>
              <input type="date" value={editEndDate} onChange={e => {
                setEditEndDate(e.target.value);
                // Send null when cleared instead of skipping the update, so an
                // end date can actually be removed (matches Trial Start above).
                updateCustomer(c.id, { trial_target_end_date: e.target.value || null });
              }} />
            </div>
          </div>

          <div className="trial-detail-section">
            <h4>Shipments</h4>
            {shipmentsLoading ? (
              <div className="trial-shipments-empty">Loading shipments…</div>
            ) : shipments.length === 0 ? (
              <div className="trial-shipments-empty">No active shipments.</div>
            ) : (
              <div className="trial-shipments-list">
                {shipments.map(s => (
                  <div key={s.tracking_number} className="trial-shipment-row">
                    <span className="trial-shipment-carrier">{s.carrier || '—'}</span>
                    <div>
                      <div className="trial-shipment-tracking">{s.tracking_number}</div>
                      <div className="trial-shipment-meta">
                        {[s.status, s.product_name, s.shipped_date?.slice(0, 10)].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="trial-shipment-actions">
                      {s.tracking_url && (
                        <>
                          <Button variant="secondary" size="sm" onClick={() => {
                            const md = `[Track shipment](${s.tracking_url})`;
                            navigator.clipboard.writeText(md).catch(() => {});
                            setCopiedTracking(s.tracking_number);
                            setTimeout(() => setCopiedTracking(prev => prev === s.tracking_number ? null : prev), 1500);
                          }}>
                            {copiedTracking === s.tracking_number ? 'Copied' : 'Copy link'}
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => window.open(s.tracking_url, '_blank', 'noopener')}>
                            Open
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="trial-detail-section">
            <h4>Email Recipients</h4>
            <div className="trial-email-fields">
              <div className="trial-email-field">
                <label>To (customer contacts)</label>
                <input type="text" value={editToEmails} onChange={e => setEditToEmails(e.target.value)}
                  placeholder="email1@company.com, email2@company.com" />
              </div>
              <div className="trial-email-field">
                <label>Names (parallel to "To" — for greetings like "Hi Jack and Jill")</label>
                <input type="text" value={editToNames} onChange={e => setEditToNames(e.target.value)}
                  placeholder="Jack Thompson, Jill Stein" />
              </div>
              <div className="trial-email-field">
                <label>CC (internal team)</label>
                <input type="text" value={editCcEmails} onChange={e => setEditCcEmails(e.target.value)}
                  placeholder="sunny@plix.ai, johann@plix.ai" />
              </div>
              <Button variant="primary" size="sm" onClick={async () => {
                const ok = await updateCustomer(c.id, {
                  to_emails: editToEmails, to_names: editToNames, cc_emails: editCcEmails,
                  trial_start_date: editStartDate || null, trial_target_end_date: editEndDate || null,
                });
                if (ok) {
                  setToast('Changes saved');
                  setTimeout(() => setToast(null), 3000);
                }
              }}>Save Changes</Button>
            </div>
          </div>

          <div className="trial-detail-section">
            <h4>Usage Stats {usageStats?.org_name && <span className="usage-org-name">({usageStats.org_name})</span>}</h4>
            {usageLoading ? (
              <p className="empty-state">Loading usage data...</p>
            ) : usageStats ? (
              <>
                <div className="usage-metrics-grid">
                  <div className="usage-metric">
                    <div className="usage-metric-value">{usageStats.device_usage.dau || 0}</div>
                    <div className="usage-metric-label">DAU</div>
                  </div>
                  <div className="usage-metric">
                    <div className="usage-metric-value">{usageStats.device_usage.wau || 0}</div>
                    <div className="usage-metric-label">WAU</div>
                  </div>
                  <div className="usage-metric">
                    <div className="usage-metric-value">{usageStats.device_usage.mau || 0}</div>
                    <div className="usage-metric-label">MAU</div>
                  </div>
                  <div className="usage-metric">
                    <div className="usage-metric-value">{usageStats.device_usage.total_devices || 0}</div>
                    <div className="usage-metric-label">Devices</div>
                  </div>
                  <div className="usage-metric">
                    <div className="usage-metric-value">{usageStats.device_usage.utilization || 0}%</div>
                    <div className="usage-metric-label">Utilization</div>
                  </div>
                </div>
                {(usageStats.org_stats.escalations != null || usageStats.org_stats.reports != null) && (
                  <div className="usage-activity">
                    <div className="usage-activity-row">
                      <span>Escalations</span><strong>{usageStats.org_stats.escalations ?? 0}</strong>
                    </div>
                    <div className="usage-activity-row">
                      <span>Reports</span><strong>{usageStats.org_stats.reports ?? 0}</strong>
                    </div>
                    <div className="usage-activity-row">
                      <span>Videos</span><strong>{usageStats.org_stats.videos_total ?? 0}</strong>
                    </div>
                    <div className="usage-activity-row">
                      <span>Activation Rate</span><strong>{usageStats.org_stats.activation_rate != null ? `${Math.round(usageStats.org_stats.activation_rate * 100)}%` : '-'}</strong>
                    </div>
                    {(() => {
                      const dur = usageStats.org_stats.total_video_duration ?? usageStats.org_stats.report_video_duration;
                      if (!dur) return null;
                      return (
                        <div className="usage-activity-row">
                          <span>Video Duration</span>
                          <strong>{formatVideoDuration(dur)}</strong>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {usageStats.device_usage.daily_trend && usageStats.device_usage.daily_trend.length > 0 && (
                  <div className="usage-trend">
                    <div className="usage-trend-label">Daily Active Devices (last {usageStats.device_usage.daily_trend.length} days)</div>
                    <div className="usage-sparkline">
                      {usageStats.device_usage.daily_trend.slice(-14).map((d, i) => {
                        const max = Math.max(...usageStats.device_usage.daily_trend.slice(-14).map(x => x.active_devices), 1);
                        return (
                          <div key={i} className="sparkline-bar-wrap" title={`${d.date}: ${d.active_devices} devices`}>
                            <div className="sparkline-bar" style={{ height: `${(d.active_devices / max) * 100}%` }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="empty-state">Usage data unavailable (customer may not be mapped to an organization yet).</p>
            )}
          </div>

          <div className="trial-detail-section">
            <h4>Stage History</h4>
            {c.history && c.history.length > 0 ? (
              <div className="trial-timeline">
                {c.history.map((h, i) => (
                  <div key={i} className="trial-timeline-item">
                    <div className="trial-timeline-dot" />
                    <div className="trial-timeline-content">
                      <strong>{h.from_stage_name ? `${h.from_stage_name} -> ${h.to_stage_name}` : h.to_stage_name}</strong>
                      <div className="trial-timeline-date">
                        {formatDateTime(h.created_at)} &middot; by {h.transitioned_by}
                        {h.time_in_stage_hours != null && <> &middot; {Math.max(0, Math.round(h.time_in_stage_hours / 24))}d in stage</>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="empty-state">No stage history yet.</p>}
          </div>

          <div className="trial-detail-section">
            <h4>Emails Sent {c.emails && c.emails.length > 0 && <span className="trial-section-count">({c.emails.length})</span>}</h4>
            {c.emails && c.emails.length > 0 ? (
              <>
                <div className="trial-email-list">
                  {(showAllEmails ? c.emails : c.emails.slice(0, 5)).map((e: any) => (
                    <div key={e.id} className="trial-email-item">
                      <div className="trial-email-subject">{e.subject}</div>
                      <div className="trial-email-meta">{e.template_name || e.trigger_type} &middot; {formatDateTime(e.sent_at)} &middot; {e.status}</div>
                    </div>
                  ))}
                </div>
                {c.emails.length > 5 && (
                  <button className="trial-show-more" onClick={() => setShowAllEmails(v => !v)}>
                    {showAllEmails ? 'Show less' : `Show ${c.emails.length - 5} more`}
                  </button>
                )}
              </>
            ) : <p className="empty-state">No emails sent yet.</p>}
          </div>

          <div className="trial-detail-section">
            <h4>Actions</h4>
            <div className="trial-action-group">
              <label>Send Email</label>
              <div className="trial-action-row">
                <select value={selectedEmailTemplate ?? ''} onChange={e => setSelectedEmailTemplate(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">Select template...</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <Button variant="primary" size="sm" disabled={!selectedEmailTemplate || sendingEmailId === c.id}
                  loading={sendingEmailId === c.id}
                  onClick={async () => {
                    if (!selectedEmailTemplate || sendingEmailId) return;
                    setSendingEmailId(c.id);
                    try {
                      const editedBody = previewBodyRef.current?.innerHTML;
                      // Forward the edited subject + body when we have a
                      // populated preview; fall back to the template if the
                      // user clicked Send before the preview rendered.
                      const payload: any = { template_id: selectedEmailTemplate };
                      if (emailPreview && editedBody) {
                        payload.subject = editedSubject || emailPreview.subject;
                        payload.body_html = editedBody;
                      }
                      await api(`/customers/${c.id}/send-email`, { method: 'POST', body: JSON.stringify(payload) });
                      setToast('Email sent');
                      setTimeout(() => setToast(null), 3000);
                      setSelectedEmailTemplate(null);
                      await openDetail(c);
                    } catch (err: any) {
                      setError(err.message);
                    } finally {
                      setSendingEmailId(null);
                    }
                  }}>
                  {sendingEmailId === c.id ? 'Sending...' : 'Send'}
                </Button>
              </div>
              {selectedEmailTemplate && (
                <div className="trial-email-preview">
                  {emailPreviewLoading && <div className="trial-email-preview-loading">Rendering preview…</div>}
                  {!emailPreviewLoading && emailPreview && (
                    <>
                      <div className="trial-email-preview-meta">
                        <div><span className="meta-label">To</span>{emailPreview.to_emails || <em>(missing)</em>}</div>
                        {emailPreview.cc_emails && (
                          <div><span className="meta-label">CC</span>{emailPreview.cc_emails}</div>
                        )}
                        <div className="trial-email-preview-subject-row">
                          <span className="meta-label">Subject</span>
                          <input
                            type="text"
                            className="trial-email-preview-subject"
                            value={editedSubject}
                            onChange={e => setEditedSubject(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="trial-email-preview-edit-hint">Click anywhere in the body to edit before sending.</div>
                      <div
                        key={`${selectedCustomer?.id}-${selectedEmailTemplate}`}
                        ref={previewBodyRef}
                        className="trial-email-preview-body trial-email-preview-body-editable"
                        contentEditable
                        suppressContentEditableWarning
                        dangerouslySetInnerHTML={{ __html: emailPreview.body_html }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="trial-action-row" style={{ marginTop: 'var(--spacing-md)', paddingTop: 'var(--spacing-md)', borderTop: '1px solid var(--color-border-light)', gap: 'var(--spacing-sm)' }}>
              <Button variant="success" size="sm" onClick={() => { updateCustomer(c.id, { status: 'converted' }); setSelectedCustomer(null); }}>Mark Converted</Button>
              <Button variant="danger" size="sm" onClick={() => { updateCustomer(c.id, { status: 'churned' }); setSelectedCustomer(null); }}>Mark Churned</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ─── Dashboard Tab ─── */
  const renderDashboard = () => (
    <>
      {dashboard?.sandbox_mode && <div className="sandbox-badge">Sandbox Mode - emails redirected internally</div>}
      <div className="trial-metrics-row">
        <div className="trial-metric-card">
          <div className="trial-metric-value">{dashboard?.active_trials ?? '-'}</div>
          <div className="trial-metric-label">Active Trials</div>
        </div>
        <div className="trial-metric-card">
          <div className="trial-metric-value">{dashboard ? `${dashboard.conversion_rate}%` : '-'}</div>
          <div className="trial-metric-label">Conversion Rate</div>
        </div>
        <div className="trial-metric-card">
          <div className="trial-metric-value">{dashboard ? `${dashboard.avg_days_to_convert}d` : '-'}</div>
          <div className="trial-metric-label">Avg Days to Convert</div>
        </div>
        <div className="trial-metric-card">
          <div className="trial-metric-value">{dashboard?.at_risk ?? '-'}</div>
          <div className="trial-metric-label">At Risk</div>
        </div>
      </div>

      <div className="trial-reactivate-bar">
        {!showReactivate ? (
          <button className="reactivate-toggle" onClick={async () => {
            setShowReactivate(true);
            try { setInactiveCustomers(await api('/inactive-customers')); } catch {}
          }}>+ Add paused, cancelled, or non-paying customer to trial</button>
        ) : (
          <div className="reactivate-form">
            <CustomerSelect
              value={reactivatingTag ?? ''}
              onChange={val => setReactivatingTag(val || null)}
              options={inactiveCustomers.map(c => ({
                value: c.customer_tag,
                label: `${trialName(c)} (${c.subscription_status})`,
              }))}
              placeholder="Search paused, cancelled, or $0 active customers..."
              className="reactivate-customer-select"
            />
            <label className="reactivate-days-label">
              <input type="number" min={1} max={30} value={reactivateDays} onChange={e => setReactivateDays(Number(e.target.value))} /> days
            </label>
            <Button variant="primary" size="sm" disabled={!reactivatingTag} onClick={async () => {
              if (!reactivatingTag) return;
              try {
                await api('/reactivate-trial', { method: 'POST', body: JSON.stringify({ customer_tag: reactivatingTag, trial_days: reactivateDays }) });
                setToast(`Trial reactivated for ${reactivatingTag}`);
                setTimeout(() => setToast(null), 3000);
                setShowReactivate(false);
                setReactivatingTag(null);
                await refreshBoard();
              } catch (err: any) {
                setError(err.message);
              }
            }}>Reactivate</Button>
            <Button variant="secondary" size="sm" onClick={() => { setShowReactivate(false); setReactivatingTag(null); }}>Cancel</Button>
          </div>
        )}
      </div>

      <div className="trial-board-controls">
        <div className="trial-sort-toggle">
          <label htmlFor="trial-sort-dir">Trial start</label>
          <select
            id="trial-sort-dir"
            value={sortDir}
            onChange={e => {
              const dir = e.target.value === 'desc' ? 'desc' : 'asc';
              setSortDir(dir);
              localStorage.setItem('trialSortDir', dir);
            }}
          >
            <option value="asc">Oldest first</option>
            <option value="desc">Newest first</option>
          </select>
        </div>

        {aeOptions.length > 0 && (
          <div className="trial-ae-filter">
            <label htmlFor="trial-ae-filter">AE</label>
            <select
              id="trial-ae-filter"
              value={aeFilter}
              onChange={e => setAeFilter(e.target.value)}
            >
              <option value="">All AEs</option>
              {aeOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {aeFilter && (
              <>
                <span className="trial-ae-filter-count">
                  {visibleCustomers.length} of {customers.length}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setAeFilter('')}>Clear</Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="trial-kanban">
        {nonTerminalStages.map(stage => {
          const stageCustomers = customersByStage[stage.id] || [];
          const isEmpty = stageCustomers.length === 0;
          const isDropTarget = dragOverStageId === stage.id && draggingId !== null;
          // A stage is either wholly manual or wholly date-sorted — a drag
          // renumbers every card in it — so any card carrying a position means
          // the trial-start sort no longer applies here.
          const isManuallyOrdered = stageCustomers.some(c => c.sort_order !== null);
          return (
            <div key={stage.id} className={`trial-kanban-column${isEmpty ? ' trial-kanban-column-empty' : ''}${isDropTarget ? ' is-drop-target' : ''}`}>
              <div className="trial-kanban-column-header">
                <div className="trial-kanban-column-header-top">
                  <span className="trial-kanban-column-title">{stage.name}</span>
                  {isManuallyOrdered && (
                    <button
                      type="button"
                      className="trial-manual-order-badge"
                      title="This stage is in a manual order, so the trial-start sort doesn't apply. Click to restore date ordering."
                      onClick={() => clearStageOrder(stage.id)}
                    >
                      Manual ×
                    </button>
                  )}
                  <span className="trial-kanban-column-count">{stageCustomers.length}</span>
                </div>
                {stageCustomers.length > 0 && (
                  <div className="trial-kanban-bulk-row">
                    <select className="bulk-template-select" value={bulkTemplateSelect[stage.id] ?? ''} onChange={e => setBulkTemplateSelect(prev => ({ ...prev, [stage.id]: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Email all...</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    {bulkTemplateSelect[stage.id] && (
                      <button className="bulk-send-btn" disabled={bulkSendingStage === stage.id} onClick={async () => {
                        const tmplId = bulkTemplateSelect[stage.id];
                        if (!tmplId) return;
                        setBulkSendingStage(stage.id);
                        let sent = 0;
                        for (const cust of stageCustomers) {
                          try {
                            await api(`/customers/${cust.id}/send-email`, { method: 'POST', body: JSON.stringify({ template_id: tmplId }) });
                            sent++;
                          } catch {}
                        }
                        setToast(`Sent to ${sent} customer${sent !== 1 ? 's' : ''}`);
                        setTimeout(() => setToast(null), 3000);
                        setBulkSendingStage(null);
                        setBulkTemplateSelect(prev => ({ ...prev, [stage.id]: null }));
                      }}>
                        {bulkSendingStage === stage.id ? 'Sending...' : 'Send'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div
                className="trial-kanban-column-body"
                onDragOver={(e) => {
                  if (draggingId === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (e.target === e.currentTarget) setDragOverCardId(null);
                }}
                onDragEnter={() => { if (draggingId !== null) setDragOverStageId(stage.id); }}
                onDragLeave={(e) => {
                  // Only clear when actually leaving the column body, not when
                  // moving onto a child card.
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    setDragOverStageId(null);
                  }
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  const targetCardId = dragOverCardId;
                  const after = dropAfter;
                  setDragOverStageId(null);
                  setDragOverCardId(null);
                  const customerId = Number(e.dataTransfer.getData('text/plain'));
                  if (!customerId) return;
                  const dragged = customers.find(cc => cc.id === customerId);
                  if (!dragged) return;
                  if (dragged.current_stage_id !== stage.id) {
                    // Cross-stage drops keep their existing meaning: move stage.
                    await advanceCustomer(customerId, stage.id);
                    return;
                  }
                  // Same stage — this is a manual re-order. A drop on empty
                  // column space (no card under the pointer) sends it to the end.
                  const next = orderAfterDrop(
                    stageCustomers.map(cc => cc.id), customerId,
                    { targetId: targetCardId, after });
                  if (next) await reorderStage(stage.id, next);
                }}
              >
                {stageCustomers.map(c => {
                  // Length of the trial = trial_target_end_date - trial_start_date.
                  // Falls back to the last non-terminal stage's target_day for
                  // legacy rows that haven't been given a target end date yet.
                  const trialDays = trialLengthDays(c.trial_start_date, c.trial_target_end_date);
                  const lastStageDay = nonTerminalStages.length > 0 ? nonTerminalStages[nonTerminalStages.length - 1].target_day : 0;
                  const target = trialDays ?? (lastStageDay || 10);
                  const elapsed = Math.max(0, c.days_elapsed ?? 0);
                  // Future-dated trials show "Starts in Nd" instead of Day 0 of N.
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const startDate = c.trial_start_date ? new Date(c.trial_start_date) : null;
                  const daysUntilStart = startDate
                    ? Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                    : 0;
                  const notStartedYet = daysUntilStart > 0;
                  const hoursInStage = Math.max(0, c.hours_in_current_stage ?? 0);
                  // No-usage badge removed along with the frontend usage cache;
                  // surfacing it required preloading every customer, which led
                  // to stale data in the detail panel.
                  return (
                    <div
                      key={c.id}
                      className={`trial-card${draggingId === c.id ? ' is-dragging' : ''}`
                        + (dragOverCardId === c.id && draggingId !== null && draggingId !== c.id
                            ? (dropAfter ? ' drop-below' : ' drop-above') : '')}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', String(c.id));
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggingId(c.id);
                      }}
                      onDragEnd={() => {
                        setDraggingId(null);
                        setDragOverStageId(null);
                        setDragOverCardId(null);
                      }}
                      onDragOver={(e) => {
                        if (draggingId === null) return;
                        e.preventDefault();
                        // Past the midpoint means insert below this card.
                        const box = e.currentTarget.getBoundingClientRect();
                        setDragOverCardId(c.id);
                        setDropAfter(e.clientY > box.top + box.height / 2);
                      }}
                      onClick={() => openDetail(c)}
                    >
                      <div className="trial-card-header">
                        <div className="trial-card-name">{trialName(c)}</div>
                        {c.assigned_rep && <div className="trial-card-rep">{c.assigned_rep}</div>}
                      </div>
                      <div className="trial-card-progress">
                        <div className="trial-card-progress-bar">
                          <div className="trial-card-progress-fill" style={{
                            width: notStartedYet ? '0%' : `${Math.min(100, (elapsed / target) * 100)}%`,
                            backgroundColor: notStartedYet ? 'var(--color-border)' : progressColor(elapsed, target),
                          }} />
                        </div>
                        <div className="trial-card-progress-text">
                          {notStartedYet
                            ? `Starts in ${daysUntilStart}d (${target}-day trial)`
                            : `Day ${elapsed} of ${target}`}
                          &nbsp;&middot;&nbsp;{Math.max(0, Math.round(hoursInStage / 24))}d in stage
                        </div>
                      </div>
                      {stage.name === 'Trial Start' && (
                        <div className="trial-card-meeting" onClick={e => e.stopPropagation()}>
                          <label className="meeting-label">Onboarding date</label>
                          <input
                            type="date"
                            className="meeting-date-input"
                            value={c.meeting_date || ''}
                            onChange={e => updateCustomer(c.id, { meeting_date: e.target.value || null })}
                          />
                        </div>
                      )}
                      <div className="trial-card-footer" onClick={e => e.stopPropagation()}>
                        <select
                          className="trial-advance-select"
                          value=""
                          disabled={advancingCustomerId === c.id}
                          onChange={e => {
                            if (e.target.value) advanceCustomer(c.id, Number(e.target.value));
                          }}
                        >
                          <option value="">{advancingCustomerId === c.id ? 'Moving...' : 'Move to...'}</option>
                          {stages.filter(s => s.id !== c.current_stage_id).map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <label className="trial-auto-toggle">
                          <input type="checkbox" checked={c.auto_advance_enabled} onChange={e => updateCustomer(c.id, { auto_advance_enabled: e.target.checked })} />
                          Auto
                        </label>
                      </div>
                    </div>
                  );
                })}
                {stageCustomers.length === 0 && (
                  <div className="trial-kanban-empty">
                    {dragOverStageId === stage.id ? 'Drop to move here' : 'Empty'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  /* ─── Config Tab ─── */
  const renderConfig = () => (
    <div className="trial-config">
      <div className="trial-config-section">
        <h3>Pipeline Stages</h3>
        <div className="trial-table-wrapper">
          <table className="trial-stages-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Auto-advance Day</th>
                <th>Auto</th>
                <th>On Enter</th>
                <th>During</th>
                <th>On Exit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...stages]
                .filter(s => !s.is_terminal)
                .sort((a, b) => a.display_order - b.display_order)
                .map(stage => {
                const terminal = false;
                return (
                <tr key={stage.id} className={terminal ? 'terminal-row' : ''}>
                  <td className="stage-name-cell">
                    {terminal ? (
                      <>
                        {stage.name}
                        <span className="terminal-badge">terminal</span>
                      </>
                    ) : (
                      <input
                        type="text"
                        className="stage-name-input"
                        value={stage.name}
                        onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, name: e.target.value } : s))}
                      />
                    )}
                  </td>
                  <td>
                    {!terminal && (
                      <input type="number" className="stage-day-input" value={stage.target_day}
                        onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, target_day: Number(e.target.value) } : s))} />
                    )}
                  </td>
                  <td>
                    {!terminal && (
                      <input type="checkbox" checked={Boolean(stage.auto_advance)}
                        onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, auto_advance: e.target.checked } : s))} />
                    )}
                  </td>
                  <td>
                    {!terminal && (
                      <select value={stage.on_enter_template_id ?? ''} onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, on_enter_template_id: e.target.value ? Number(e.target.value) : null } : s))}>
                        <option value="">-</option>
                        {templates.filter(t => t.template_type === 'enter').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    {!terminal && (
                      <div className="during-cell">
                        <select value={stage.during_template_id ?? ''} onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, during_template_id: e.target.value ? Number(e.target.value) : null } : s))}>
                          <option value="">-</option>
                          {templates.filter(t => t.template_type === 'during').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        {stage.during_template_id && (
                          <span className="during-freq">
                            send after <input type="number" min={1} max={30} value={stage.during_frequency_days}
                              onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, during_frequency_days: Number(e.target.value) } : s))}
                            /> days in stage (once)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {!terminal && (
                      <select value={stage.on_exit_template_id ?? ''} onChange={e => setStages(prev => prev.map(s => s.id === stage.id ? { ...s, on_exit_template_id: e.target.value ? Number(e.target.value) : null } : s))}>
                        <option value="">-</option>
                        {templates.filter(t => t.template_type === 'exit').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="stage-actions-cell">
                    {!terminal && (
                      <>
                        <Button variant="ghost" size="sm" title="Move up"
                          onClick={() => moveStage(stage.id, -1)}>↑</Button>
                        <Button variant="ghost" size="sm" title="Move down"
                          onClick={() => moveStage(stage.id, 1)}>↓</Button>
                        <Button variant="outline" size="sm"
                          onClick={() => api(`/stages/${stage.id}`, { method: 'PUT', body: JSON.stringify(stage) }).then(() => api('/stages').then(setStages))}>Save</Button>
                        <Button variant="ghost" size="sm" title="Delete stage"
                          onClick={() => deleteStage(stage)}>✕</Button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="trial-add-row">
          <input type="text" placeholder="New stage name" value={newStageName} onChange={e => setNewStageName(e.target.value)} />
          <input type="number" placeholder="Day" value={newStageTarget} onChange={e => setNewStageTarget(Number(e.target.value))} min={0} style={{ width: 70 }} />
          <Button variant="primary" size="sm" disabled={!newStageName.trim()} onClick={async () => {
            await api('/stages', { method: 'POST', body: JSON.stringify({ name: newStageName.trim(), target_day: newStageTarget, display_order: stages.length, auto_advance: false, is_terminal: false }) });
            setNewStageName('');
            setNewStageTarget(3);
            setStages(await api('/stages'));
          }}>Add Stage</Button>
        </div>
      </div>

      <div className="trial-config-section">
        <h3>Email Templates</h3>
        <div className="trial-template-groups">
          {['enter', 'during', 'exit', 'followup', 'escalation', 'stats'].map(type => {
            const group = templates.filter(t => t.template_type === type);
            if (group.length === 0) return null;
            return (
              <div key={type} className="trial-template-type-group">
                <div className="trial-template-type-label">{type === 'followup' ? 'Follow-up' : type.charAt(0).toUpperCase() + type.slice(1)}</div>
                <div className="trial-template-type-chips">
                  {group.map(t => (
                    <button key={t.id} className={`trial-template-chip ${editingTemplate?.id === t.id ? 'active' : ''}`} onClick={() => {
                      setEditingTemplate({ ...t });
                      setEditorMarkdown(htmlToMarkdown(t.body_html));
                    }}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <button className="trial-template-chip add-chip" onClick={() => {
            setEditingTemplate({ id: 0, name: '', subject: '', body_html: '', template_type: 'enter' });
            setEditorMarkdown('');
          }}>+ New Template</button>
        </div>
        {editingTemplate && (
          <div className="trial-template-editor">
            <div className="editor-top-row">
              <div className="editor-field" style={{ flex: 2 }}>
                <label>Name</label>
                <input type="text" value={editingTemplate.name} onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })} />
              </div>
              <div className="editor-field" style={{ flex: 1 }}>
                <label>Type</label>
                <select value={editingTemplate.template_type} onChange={e => setEditingTemplate({ ...editingTemplate, template_type: e.target.value })}>
                  <option value="enter">Enter</option>
                  <option value="during">During</option>
                  <option value="exit">Exit</option>
                  <option value="followup">Follow-up</option>
                  <option value="escalation">Escalation</option>
                  <option value="stats">Stats</option>
                </select>
              </div>
            </div>
            <div className="editor-field">
              <label>Subject</label>
              <input type="text" value={editingTemplate.subject} onChange={e => setEditingTemplate({ ...editingTemplate, subject: e.target.value })} />
            </div>
            <div className="editor-field">
              <label>Variables</label>
              <div className="trial-var-groups">
                {templateVarGroups.map(group => (
                  <div key={group.label} className="trial-var-group">
                    <span className="trial-var-group-label">{group.label}</span>
                    {group.vars.map(v => (
                      <button key={v} className="trial-var-btn" type="button" onClick={() => {
                        const ta = editorRef.current;
                        if (ta) {
                          const start = ta.selectionStart;
                          const before = editorMarkdown.substring(0, start);
                          const after = editorMarkdown.substring(ta.selectionEnd);
                          const newMd = before + v + after;
                          setEditorMarkdown(newMd);
                          setEditingTemplate({ ...editingTemplate, body_html: marked.parse(newMd) as string });
                          setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + v.length; }, 0);
                        } else {
                          const newMd = editorMarkdown + v;
                          setEditorMarkdown(newMd);
                          setEditingTemplate({ ...editingTemplate, body_html: marked.parse(newMd) as string });
                        }
                      }}>{v.replace(/\{\{|\}\}/g, '')}</button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="editor-split-pane">
              <div className="editor-pane editor-pane-left">
                <div className="editor-pane-header">Compose (Markdown)</div>
                <textarea
                  ref={editorRef}
                  value={editorMarkdown}
                  onChange={e => {
                    setEditorMarkdown(e.target.value);
                    setEditingTemplate({ ...editingTemplate, body_html: marked.parse(e.target.value) as string });
                  }}
                  placeholder={"Hi {{customer_name}},\n\nWrite your email here using **bold**, *italic*, and - bullet lists.\n\nInsert variables using the buttons above."}
                />
              </div>
              <div className="editor-pane editor-pane-right">
                <div className="editor-pane-header">Preview</div>
                <div className="editor-preview" dangerouslySetInnerHTML={{ __html: renderPreview(editorMarkdown) }} />
              </div>
            </div>
            <div className="editor-actions">
              {editingTemplate.id ? (
                <Button variant="danger" size="sm" onClick={() => deleteTemplate(editingTemplate)}>Delete</Button>
              ) : <span />}
              <div className="editor-actions-right">
                <Button variant="secondary" size="sm" onClick={() => { setEditingTemplate(null); setEditorMarkdown(''); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={async () => {
                  const toSave = { ...editingTemplate, body_html: marked.parse(editorMarkdown) as string };
                  const url = toSave.id ? `/templates/${toSave.id}` : '/templates';
                  await api(url, { method: toSave.id ? 'PUT' : 'POST', body: JSON.stringify(toSave) });
                  setEditingTemplate(null);
                  setEditorMarkdown('');
                  setTemplates(await api('/templates'));
                }}>{editingTemplate.id ? 'Save' : 'Create'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="trial-config-section">
        <h3>Follow-Up Rules</h3>
        <div className="trial-table-wrapper">
          <table className="trial-rules-table">
            <thead>
              <tr><th>Rule Name</th><th>Trigger</th><th>Threshold</th><th>Template</th><th>Final Warning</th><th>Status</th></tr>
            </thead>
            <tbody>
              {followUpRules.map((rule, idx) => (
                <tr key={rule.id ?? idx} className={!rule.is_active ? 'paused-row' : ''}>
                  <td>{rule.name}</td>
                  <td><span className="trigger-badge">{rule.trigger_condition.replace(/_/g, ' ')}</span></td>
                  <td>{rule.days_threshold} days</td>
                  <td>{rule.template_name || '-'}</td>
                  <td>{rule.is_final_warning ? 'Yes' : '-'}</td>
                  <td>
                    <button
                      className={`rule-status-toggle ${rule.is_active ? 'active' : 'paused'}`}
                      onClick={() => {
                        if (!rule.id) return;
                        const toggled = !rule.is_active;
                        setFollowUpRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: toggled } : r));
                        api(`/followup-rules/${rule.id}`, {
                          method: 'PUT',
                          body: JSON.stringify({ is_active: toggled }),
                        }).catch(() => setFollowUpRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !toggled } : r)));
                      }}
                    >
                      {rule.is_active ? 'Active' : 'Paused'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (loading) return <LoadingSpinner message="Loading trial pipeline..." />;

  return (
    <div className="trial-pipeline-page">
      {error && <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>}
      {toast && <div className="trial-toast">{toast}</div>}
      <div className="trial-tabs">
        <button className={`trial-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
        <button className={`trial-tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>Configuration</button>
      </div>
      {activeTab === 'dashboard' ? renderDashboard() : renderConfig()}
      {renderDetail()}
    </div>
  );
};

export default TrialPipelinePage;
